/**
 * Renders one template data field (every FieldType in the form-engine
 * schema). Values are plain JSON stored in answers.fields[key]; files are
 * attachment ids.
 */
import { useMemo, useState } from 'react';
import { acceptAttribute, evaluateFormula, localize, ratingRange, type ChoiceOption, type FieldItem } from '@sss/form-engine';
import { Banner, Button, Checkbox, Chips, Field, Icon, IconButton, Input, RadioList, Segmented, Select, Sheet, Textarea } from '../../components';
import { useI18n } from '../../lib/i18n';
import { currentPosition, type Position } from '../../lib/native';
import { eligiblePeople } from '../../lib/sync/pack';
import { PhotoInput } from './PhotoInput';
import { SignaturePad } from './SignaturePad';
import type { CaptureActions, CaptureLookup } from './types';

export interface FieldRendererProps {
  field: FieldItem;
  value: unknown;
  onChange: (v: unknown) => void;
  lang: string;
  sourceLanguage: string;
  disabled?: boolean;
  error?: string | null;
  /** All field values (or the row's, inside a repeating group), for calculated fields not yet stored. */
  fields: Record<string, unknown>;
  actions: CaptureActions;
  lookup: CaptureLookup;
  /** Analyst note on a returned survey. */
  flagNote?: string;
}

export function HelpButton({ title, text }: { title: string; text: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton icon="help" label={t('ui.capture.help')} size="sm" onClick={() => setOpen(true)} className="-my-2 -me-2 text-brand" />
      <Sheet open={open} onClose={() => setOpen(false)} title={title} closeLabel={t('ui.common.close')}>
        <p className="whitespace-pre-line text-base leading-relaxed">{text}</p>
      </Sheet>
    </>
  );
}

function OptionHints({ options, selected, lang, src }: { options: ChoiceOption[]; selected: string[]; lang: string; src: string }) {
  const hints = options.filter((o) => o.hint && selected.includes(o.code));
  if (!hints.length) return null;
  return (
    <div className="flex flex-col gap-2">
      {hints.map((o) => (
        <Banner key={o.code} tone="info" title={localize(o.label, lang, src)}>
          {localize(o.hint, lang, src)}
        </Banner>
      ))}
    </div>
  );
}

function GpsField({ value, onChange, disabled }: { value: unknown; onChange: (v: Position | null) => void; disabled?: boolean }) {
  const { t, fmt } = useI18n();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pos = value as Position | null | undefined;
  const capture = async () => {
    setBusy(true);
    setErr(null);
    try {
      onChange(await currentPosition());
    } catch (e) {
      setErr(e instanceof Error && e.message === 'GEOLOCATION_DENIED' ? t('ui.capture.gpsDenied') : t('ui.capture.gpsFailed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      {pos && (
        <div className="flex items-center gap-2 rounded-xl bg-surface-2 p-3 text-sm">
          <Icon name="mapPin" className="text-brand" />
          <span className="font-mono">
            {pos.lat.toFixed(6)}, {pos.lng.toFixed(6)}
          </span>
          <span className="text-muted">± {fmt.number(pos.accuracy, 0)} m</span>
        </div>
      )}
      {!disabled && (
        <Button variant="secondary" icon="navigation" onClick={capture} loading={busy}>
          {pos ? t('ui.capture.gpsRecapture') : t('ui.capture.gpsCapture')}
        </Button>
      )}
      {err && <p className="text-sm text-danger">{err}</p>}
    </div>
  );
}

export function FieldRenderer(p: FieldRendererProps) {
  const { field, value, onChange, lang, sourceLanguage: src, disabled, error, actions, lookup } = p;
  const { t, fmt } = useI18n();
  const [sig, setSig] = useState(false);
  const label = localize(field.label, lang, src);
  const help = field.help ? localize(field.help, lang, src) : '';
  const placeholder = field.placeholder ? localize(field.placeholder, lang, src) : undefined;
  const opts = field.options ?? [];

  if (field.type === 'hidden') return null;

  const control = (id: string, describedBy: string | undefined) => {
    const common = { id, disabled, 'aria-describedby': describedBy, invalid: !!error };
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <Input
            {...common}
            type={field.type === 'phone' ? 'tel' : field.type}
            inputMode={field.type === 'phone' ? 'tel' : field.type === 'email' ? 'email' : undefined}
            autoComplete="off"
            placeholder={placeholder}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case 'textarea':
      case 'address':
        return <Textarea {...common} placeholder={placeholder} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;
      case 'number':
        return (
          <div className="flex items-center gap-2">
            <Input
              {...common}
              type="number"
              inputMode="decimal"
              min={field.min}
              max={field.max}
              step={field.step ?? 'any'}
              value={value === undefined || value === null ? '' : String(value)}
              onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            />
            {field.unit && <span className="shrink-0 text-muted">{field.unit}</span>}
          </div>
        );
      case 'date':
        return <Input {...common} type="date" value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;
      case 'time':
        return <Input {...common} type="time" value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;
      case 'datetime':
        return <Input {...common} type="datetime-local" value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;
      case 'yes_no':
        return (
          <Segmented<'yes' | 'no'>
            label={label}
            disabled={disabled}
            size="md"
            value={value === true ? 'yes' : value === false ? 'no' : null}
            onChange={(v) => onChange(v === null ? null : v === 'yes')}
            allowDeselect
            options={[
              { value: 'yes', label: t('common.yes') },
              { value: 'no', label: t('common.no') },
            ]}
          />
        );
      case 'rating': {
        const { min, max } = ratingRange(field);
        const n = typeof value === 'number' ? value : min - 1;
        return (
          <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={label}>
            {Array.from({ length: max - min + 1 }, (_, i) => i + min).map((i) => (
              <button
                key={i}
                type="button"
                role="radio"
                aria-checked={n === i}
                aria-label={String(i)}
                disabled={disabled}
                onClick={() => onChange(n === i ? null : i)}
                className={i <= n ? 'tap text-warning' : 'tap text-border'}
              >
                <Icon name="star" size={30} fill={i <= n ? 'currentColor' : 'none'} />
              </button>
            ))}
          </div>
        );
      }
      case 'single_choice': {
        const v = (value as string) ?? null;
        const display = field.display ?? (opts.length <= 3 ? 'buttons' : opts.length <= 6 ? 'radio' : 'dropdown');
        if (display === 'dropdown')
          return (
            <Select {...common} value={v ?? ''} onChange={(e) => onChange(e.target.value || null)}>
              <option value="">{t('ui.capture.choose')}</option>
              {opts.map((o) => (
                <option key={o.code} value={o.code}>
                  {localize(o.label, lang, src)}
                </option>
              ))}
            </Select>
          );
        if (display === 'buttons' && opts.length <= 4)
          return (
            <Segmented
              label={label}
              size="md"
              disabled={disabled}
              value={v}
              allowDeselect
              onChange={(x) => onChange(x)}
              options={opts.map((o) => ({ value: o.code, label: localize(o.label, lang, src) }))}
            />
          );
        if (display === 'chips' || display === 'buttons')
          return (
            <Chips
              disabled={disabled}
              selected={v ? [v] : []}
              onToggle={(x) => onChange(x === v ? null : x)}
              options={opts.map((o) => ({ value: o.code, label: localize(o.label, lang, src) }))}
            />
          );
        return (
          <RadioList
            name={field.key}
            disabled={disabled}
            value={v}
            onChange={(x) => onChange(x)}
            options={opts.map((o) => ({ value: o.code, label: localize(o.label, lang, src) }))}
          />
        );
      }
      case 'multi_choice': {
        const sel = Array.isArray(value) ? (value as string[]) : [];
        const toggle = (code: string) => onChange(sel.includes(code) ? sel.filter((x) => x !== code) : [...sel, code]);
        if (field.display === 'chips')
          return <Chips disabled={disabled} selected={sel} onToggle={toggle} options={opts.map((o) => ({ value: o.code, label: localize(o.label, lang, src) }))} />;
        return (
          <div className="flex flex-col gap-2">
            {opts.map((o) => (
              <Checkbox key={o.code} disabled={disabled} checked={sel.includes(o.code)} onChange={() => toggle(o.code)} label={localize(o.label, lang, src)} />
            ))}
          </div>
        );
      }
      case 'photo':
      case 'file':
        return (
          <PhotoInput
            ids={Array.isArray(value) ? (value as string[]) : []}
            max={field.maxPhotos ?? (field.type === 'photo' ? 10 : 5)}
            disabled={disabled}
            // Only what the server accepts (C5); photos are re-encoded as JPEG before upload.
            accept={field.type === 'photo' ? 'image/*' : acceptAttribute('FILE')}
            getUrl={actions.fileUrl}
            getMeta={actions.fileMeta}
            // The answers are updated by the actions, from their current state (not this render's value).
            onAdd={async (files) => {
              await actions.addFiles({ field: field.key }, field.type === 'photo' ? 'PHOTO' : 'FILE', files);
            }}
            onRemove={(id) => actions.removeFile(id)}
          />
        );
      case 'signature':
        return (
          <div className="flex flex-col gap-2">
            {typeof value === 'string' && (
              <PhotoInput
                ids={[value]}
                max={1}
                getUrl={actions.fileUrl}
                disabled={disabled}
                onAdd={async (files) => {
                  await actions.addFiles({ field: field.key }, 'SIGNATURE', files);
                }}
                onRemove={(id) => actions.removeFile(id)}
              />
            )}
            {!disabled && !value && (
              <Button variant="secondary" icon="pen" onClick={() => setSig(true)}>
                {t('ui.capture.sign')}
              </Button>
            )}
            <SignaturePad
              open={sig}
              onClose={() => setSig(false)}
              onSave={async (png) => {
                setSig(false);
                await actions.addFiles({ field: field.key }, 'SIGNATURE', [png]);
              }}
            />
          </div>
        );
      case 'gps':
        return <GpsField value={value} onChange={onChange} disabled={disabled} />;
      case 'customer':
      case 'site':
      case 'branch': {
        const text = field.type === 'customer' ? lookup.customerName : field.type === 'site' ? lookup.siteName : lookup.branch;
        return <Input {...common} value={text ?? ''} readOnly disabled />;
      }
      case 'user':
        return <PersonPicker id={id} describedBy={describedBy} field={field} value={value} onChange={onChange} disabled={disabled} lookup={lookup} invalid={!!error} />;
      case 'calculated': {
        // Stored in the answers on every change (and recomputed by the server); computed here only as a fallback.
        const result = typeof value === 'number' ? value : evaluateFormula(field.formula ?? '', p.fields);
        return (
          <output id={id} className="rounded-xl bg-surface-2 px-3.5 py-3 text-lg font-semibold tabular-nums">
            {result === null ? '—' : fmt.number(result, 2)} {field.unit}
          </output>
        );
      }
      case 'repeating_group':
        return <RepeatingGroup {...p} />;
      default:
        return null;
    }
  };

  return (
    <div id={`item-${field.id}`} className="scroll-mt-24">
      {p.flagNote && (
        <Banner tone="warning" title={t('ui.capture.analystNote')} className="mb-2">
          {p.flagNote}
        </Banner>
      )}
      <Field label={label} required={field.required} error={error} labelAction={help ? <HelpButton title={label} text={help} /> : undefined}>
        {control}
      </Field>
      {(field.type === 'single_choice' || field.type === 'multi_choice') && (
        <div className="mt-2">
          <OptionHints options={opts} selected={Array.isArray(value) ? (value as string[]) : value ? [value as string] : []} lang={lang} src={src} />
        </div>
      )}
    </div>
  );
}

/**
 * Person field (C10): a pick list of the people in the offline pack who hold
 * one of the field's roles at this survey's site. The answer is the person's
 * id; desk screens and the PDF show the name.
 */
function PersonPicker({
  id,
  describedBy,
  field,
  value,
  onChange,
  disabled,
  lookup,
  invalid,
}: {
  id: string;
  describedBy: string | undefined;
  field: FieldItem;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
  lookup: CaptureLookup;
  invalid: boolean;
}) {
  const { t } = useI18n();
  const [q, setQ] = useState('');
  const all = useMemo(() => eligiblePeople(lookup.people ?? [], lookup.site, field.roleFilter), [lookup.people, lookup.site, field.roleFilter]);
  const current = typeof value === 'string' ? value : '';
  const known = all.find((p) => p.id === current) ?? (lookup.people ?? []).find((p) => p.id === current);
  const needle = q.trim().toLocaleLowerCase();
  const shown = needle ? all.filter((p) => p.name.toLocaleLowerCase().includes(needle) || p.id === current) : all;
  if (all.length === 0 && !current) return <p className="text-sm text-muted">{t('ui.capture.personNone')}</p>;
  return (
    <div className="flex flex-col gap-2">
      {all.length > 12 && !disabled && (
        <Input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ui.capture.personSearch')} aria-label={t('ui.capture.personSearch')} />
      )}
      <Select id={id} aria-describedby={describedBy} invalid={invalid} disabled={disabled} value={current} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">{t('ui.capture.choose')}</option>
        {current && !shown.some((p) => p.id === current) && <option value={current}>{known?.name ?? t('ui.capture.personUnknown')}</option>}
        {shown.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </Select>
    </div>
  );
}

function RepeatingGroup(p: FieldRendererProps) {
  const { field, value, onChange, disabled, lang, sourceLanguage: src } = p;
  const { t } = useI18n();
  const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
  const canAdd = !disabled && (field.maxRows === undefined || rows.length < field.maxRows);
  const canRemove = !disabled && rows.length > (field.minRows ?? 0);
  const setRow = (i: number, key: string, v: unknown) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
  return (
    <div className="flex flex-col gap-3">
      {rows.map((row, i) => (
        <div key={i} className="rounded-xl border border-border bg-surface-2/50 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-muted">
              {localize(field.label, lang, src)} {i + 1}
            </span>
            {canRemove && <IconButton icon="trash" size="sm" label={t('ui.capture.removeRow')} onClick={() => onChange(rows.filter((_, j) => j !== i))} />}
          </div>
          <div className="flex flex-col gap-3">
            {(field.fields ?? []).map((sub) => (
              <FieldRenderer
                key={sub.key}
                {...p}
                flagNote={undefined}
                error={null}
                field={sub}
                value={row[sub.key]}
                fields={row}
                onChange={(v) => setRow(i, sub.key, v)}
              />
            ))}
          </div>
        </div>
      ))}
      {canAdd && (
        <Button variant="secondary" icon="plus" onClick={() => onChange([...rows, {}])}>
          {t('ui.capture.addRow')}
        </Button>
      )}
    </div>
  );
}
